// src/pages/orders/OrderListPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ShoppingCart,
  Plus,
  Eye,
  Edit,
  Package,
  User,
  MapPin,
  Calendar,
  Phone as PhoneIcon,
  Store,
  CheckCircle,
  Clock,
  XCircle,
  Truck,
  RefreshCw,
  AlertCircle,
  Copy,
  Check,
  X,
  Search,
  Hash,
  Printer,
  SlidersHorizontal
} from 'lucide-react';
import {
  PageHeader,
  DataTable,
  FilterPanel
} from '../../components/common';
import DateRangePicker from '../../components/ui/DateRangePicker/DateRangePicker';
import { getEcomOrders, exportEcomOrders } from '../../services/api/orders';
import { getAllOrderReferenceData } from '../../services/api/orderReference';
import { getShops } from '../../services/api/shops';
import { getBusinessCouriers, getCourierStatuses } from '../../services/api/businessCourier';
import { getProducts } from '../../services/api/productSearch';
import { getShiftTypes } from '../../services/api/messageCenterShifts';
import { getUsers } from '../../services/api/users';
import { usePermissions } from '../../hooks/usePermissions';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { useExport } from '../../contexts/ExportContext';
import { formatCurrency } from '../../utils/orderCalculations';
import { formatDate, formatTime, formatDateTime } from '../../utils/dateUtils';
import { PaymentMethodIcon } from '../../utils/paymentMethodIcons';
import OrderDetailsModal from './components/OrderDetailsModal';
import AirwaybillTrackingModal from './components/AirwaybillTrackingModal';
import OrderFormFieldSettingsModal from './components/OrderFormFieldSettingsModal';
import OfflineSalesOrderDetailsModal from '../offlineSales/components/OfflineSalesOrderDetailsModal';
import { isOfflineChannel } from '../../config/app.config';
import styles from './OrderListPage.module.css';

/**
 * Normalizes a search query for phone number lookups.
 * If the query looks like a local phone number starting with 0 (e.g. 0771234567),
 * the leading 0 is stripped so it matches the +94XXXXXXXXX format stored in the DB.
 * Also handles arrays of search terms (multiSearch mode).
 */
const normalizeSearchQuery = (query) => {
  if (!query) return query;

  if (Array.isArray(query)) {
    return query.map((term) => {
      const trimmed = term.trim();
      return /^0\d{3,9}$/.test(trimmed) ? trimmed.slice(1) : trimmed;
    });
  }

  const trimmed = query.trim();
  if (/^0\d{3,9}$/.test(trimmed)) {
    return trimmed.slice(1);
  }
  return trimmed;
};

// ─── NOT_UPDATED sentinel value ───────────────────────────────────────────────
// Used in the multiselect options list. When present as the only selection the
// backend receives courierStatusNotUpdated=true instead of courierStatusIds.
const NOT_UPDATED_VALUE = '__NOT_UPDATED__';

// ─── Product Search Filter ────────────────────────────────────────────────────
const ProductFilter = ({ selectedProductIds = [], onChange }) => {
  const [searchTerm, setSearchTerm]       = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching]     = useState(false);
  const [showResults, setShowResults]     = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const searchTimeoutRef = useRef(null);
  const wrapperRef       = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedProductIds.length === 0) setSelectedProducts([]);
  }, [selectedProductIds]);

  const performSearch = async (term) => {
    if (!term.trim()) { setSearchResults([]); setShowResults(false); return; }
    try {
      setIsSearching(true);
      const response = await getProducts({ search: term.trim(), limit: 20, page: 1 });
      setSearchResults((response.products || []).filter(p => p.type !== 'bundle'));
      setShowResults(true);
    } catch (err) {
      console.error('Product search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => performSearch(value), 300);
  };

  const handleSelect = (product) => {
    if (selectedProductIds.includes(product.productId)) return;
    const newSelected = [...selectedProducts, product];
    const newIds      = [...selectedProductIds, product.productId];
    setSelectedProducts(newSelected);
    onChange(newIds);
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
  };

  const handleRemove = (productId) => {
    const newSelected = selectedProducts.filter(p => p.productId !== productId);
    const newIds      = selectedProductIds.filter(id => id !== productId);
    setSelectedProducts(newSelected);
    onChange(newIds);
  };

  const handleClearAll = () => { setSelectedProducts([]); onChange([]); };

  return (
    <div className={styles.productFilter} ref={wrapperRef}>
      {selectedProducts.length > 0 && (
        <div className={styles.productFilterTags}>
          {selectedProducts.map((product) => (
            <div key={product.productId} className={styles.productFilterTag}>
              <span>{product.displayName}</span>
              <button type="button" onClick={() => handleRemove(product.productId)} className={styles.productFilterTagRemove}>
                <X size={12} />
              </button>
            </div>
          ))}
          <button type="button" onClick={handleClearAll} className={styles.productFilterClearAll}>Clear all</button>
        </div>
      )}

      <div className={styles.productFilterInputWrapper}>
        <Search size={14} className={styles.productFilterSearchIcon} />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => searchResults.length > 0 && setShowResults(true)}
          placeholder="Search products…"
          className={styles.productFilterInput}
        />
        {searchTerm && (
          <button type="button" onClick={() => { setSearchTerm(''); setSearchResults([]); setShowResults(false); }} className={styles.productFilterInputClear}>
            <X size={12} />
          </button>
        )}
      </div>

      {showResults && (
        <div className={styles.productFilterDropdown}>
          {isSearching ? (
            <div className={styles.productFilterLoading}>
              <div className={styles.productFilterSpinner} />
              <span>Searching…</span>
            </div>
          ) : searchResults.length > 0 ? (
            searchResults
              .filter(p => !selectedProductIds.includes(p.productId))
              .map((product) => (
                <div key={product.productId} className={styles.productFilterOption} onClick={() => handleSelect(product)}>
                  <div className={styles.productFilterOptionName}><Package size={13} />{product.displayName}</div>
                  <div className={styles.productFilterOptionMeta}><Hash size={11} />{product.sku || 'No SKU'}</div>
                </div>
              ))
          ) : (
            <div className={styles.productFilterEmpty}><Package size={15} /><span>No products found</span></div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const OrderListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const { settings: messageCenterSettings } = useBusinessSettings('messageCenter');
  const { t } = useLocalization();
  const { success, error: showError, confirm } = useAlert();
  const { startExport, isExporting } = useExport();

  const [orders, setOrders]             = useState([]);
  const [isLoading, setIsLoading]       = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal]         = useState(false);
  const [trackingOrder, setTrackingOrder]               = useState(null);
  const [showFieldSettings, setShowFieldSettings]       = useState(false);
  const [copiedStates, setCopiedStates]                 = useState({});
  const [isInitialized, setIsInitialized]               = useState(false);

  // Selection state
  const [selectedOrders, setSelectedOrders] = useState([]);

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [10, 20, 50, 100, 500, 1000]
  });

  // Sorting
  const [sorting, setSorting] = useState({
    orderBy: 'createdAt',
    orderDirection: 'DESC'
  });

  // Filters
  const getInitialFilters = () => {
    const urlSearch = searchParams.get('search');
    return {
      search: urlSearch || '',
      shopIds: [],
      cityIds: [],
      statusKeys: [],
      paymentTypeKey: '',
      createdAtRange: { from: '', to: '' },       // replaces fromDate / toDate
      dispatchedDateRange: { from: '', to: '' },  // new
      shiftDateRange: { from: '', to: '' },
      shiftTypeIds: [],
      businessCourierIds: [],
      courierStatusIds: [],   // may contain NOT_UPDATED_VALUE sentinel
      productIds: [],
      confirmedByUserIds: [],
      confirmedAtRange: { from: '', to: '' }
    };
  };

  const [filters, setFilters]         = useState(getInitialFilters);
  const [showFilters, setShowFilters] = useState(false);

  // Reference data
  const [referenceData, setReferenceData] = useState({
    shops: [],
    cities: [],
    orderStatuses: [],
    paymentTypes: [],
    businessCouriers: [],
    courierStatuses: [],
    shiftTypes: [],
    users: []
  });

  useEffect(() => { loadReferenceData(); }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsInitialized(true), 0);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    fetchOrders();
  }, [
    isInitialized,
    pagination.currentPage,
    pagination.pageSize,
    sorting.orderBy,
    sorting.orderDirection,
    filters.search,
    filters.shopIds,
    filters.cityIds,
    filters.statusKeys,
    filters.paymentTypeKey,
    filters.createdAtRange,
    filters.dispatchedDateRange,
    filters.shiftDateRange,
    filters.shiftTypeIds,
    filters.businessCourierIds,
    filters.courierStatusIds,
    filters.productIds,
    filters.confirmedByUserIds,
    filters.confirmedAtRange
  ]);

  const loadReferenceData = async () => {
    try {
      const [data, shops, businessCouriers, courierStatuses, shiftTypesResponse, usersResponse] = await Promise.all([
        getAllOrderReferenceData(),
        getShops({ assigned: true, type: 'online,dropshipper' }),
        getBusinessCouriers(),
        getCourierStatuses(),
        hasPermission('view_message_center_shifts')
          ? getShiftTypes().catch(() => ({ shiftTypes: [] }))
          : Promise.resolve({ shiftTypes: [] }),
        getUsers({ limit: 1000 }).catch(() => ({ users: [] }))
      ]);

      setReferenceData({
        shops: Array.isArray(shops) ? shops : (shops.shops || []),
        cities: data.cities || [],
        orderStatuses: data.orderStatuses || [],
        paymentTypes: data.paymentTypes || [],
        businessCouriers: businessCouriers.couriers || [],
        courierStatuses: courierStatuses.courierStatuses || [],
        shiftTypes: shiftTypesResponse.shiftTypes || [],
        users: usersResponse?.users || []
      });
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
  };

  // Build filter params shared by fetch + export
  const buildFilterParams = () => {
    const params = {
      orderBy: sorting.orderBy,
      orderDirection: sorting.orderDirection
    };

    if (filters.search) {
      const normalizedSearch = normalizeSearchQuery(filters.search);
      if (Array.isArray(normalizedSearch) && normalizedSearch.length > 0) {
        params.multiSearch = normalizedSearch.join(',');
      } else if (typeof normalizedSearch === 'string' && normalizedSearch.trim() !== '') {
        params.search = normalizedSearch;
      }
    }

    if (filters.cityIds.length > 0)            params.cityIds            = filters.cityIds.join(',');
    if (filters.statusKeys.length > 0)         params.statusKeys         = filters.statusKeys.join(',');
    if (filters.shopIds.length > 0)            params.shopIds            = filters.shopIds.join(',');
    if (filters.paymentTypeKey)                params.paymentTypeKey     = filters.paymentTypeKey;
    if (filters.productIds.length > 0)         params.productIds         = filters.productIds.join(',');

    // createdAt date range
    if (filters.createdAtRange?.from)          params.fromDate           = filters.createdAtRange.from;
    if (filters.createdAtRange?.to)            params.toDate             = filters.createdAtRange.to;

    // dispatchedDate range
    if (filters.dispatchedDateRange?.from)     params.dispatchedDateFrom = filters.dispatchedDateRange.from;
    if (filters.dispatchedDateRange?.to)       params.dispatchedDateTo   = filters.dispatchedDateRange.to;

    // shift date range
    if (filters.shiftDateRange?.from)          params.shiftDateFrom      = filters.shiftDateRange.from;
    if (filters.shiftDateRange?.to)            params.shiftDateTo        = filters.shiftDateRange.to;
    if (filters.shiftTypeIds.length > 0)       params.shiftTypeIds       = filters.shiftTypeIds.join(',');

    // confirmedBy / confirmedAt
    if (filters.confirmedByUserIds.length > 0) params.confirmedByUserIds = filters.confirmedByUserIds.join(',');
    if (filters.confirmedAtRange?.from)        params.confirmedAtFrom    = filters.confirmedAtRange.from;
    if (filters.confirmedAtRange?.to)          params.confirmedAtTo      = filters.confirmedAtRange.to;

    // Courier filters — handle NOT_UPDATED sentinel
    const isNotUpdated = filters.courierStatusIds.includes(NOT_UPDATED_VALUE);
    if (isNotUpdated) {
      params.courierStatusNotUpdated = true;
    } else if (filters.businessCourierIds.length > 0) {
      params.businessCourierIds = filters.businessCourierIds.join(',');
    }

    // businessCourierIds — send regardless of NOT_UPDATED
    if (filters.businessCourierIds.length > 0) {
      params.businessCourierIds = filters.businessCourierIds.join(',');
    }

    // Real courier status IDs (exclude sentinel)
    const realCourierStatusIds = filters.courierStatusIds.filter(id => id !== NOT_UPDATED_VALUE);
    if (!isNotUpdated && realCourierStatusIds.length > 0) {
      params.courierStatusIds = realCourierStatusIds.join(',');
    }

    return params;
  };

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      setLoadingError(null);

      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        ...buildFilterParams()
      };

      const response = await getEcomOrders(params);
      setOrders(response.orders);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      if (err.response?.status === 403) {
        setLoadingError(t('errors.permissionDenied'));
      } else {
        setLoadingError(t('ecomOrders.failedToLoadOrder'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Export ───────────────────────────────────────────────────────────────
  const MAX_EXPORT_RECORDS = 150000;
  const EXPORT_BATCH_SIZE  = 500;

  const handleExport = async () => {
    if (isExporting) {
      showError(t('ecomOrders.exportAlreadyRunning'));
      return;
    }

    const exportCount = Math.min(pagination.total || 0, MAX_EXPORT_RECORDS);
    const confirmed = await confirm(
      `${t('ecomOrders.exportConfirmMessage', { count: exportCount.toLocaleString() })}${
        (pagination.total || 0) > MAX_EXPORT_RECORDS
          ? ` ${t('ecomOrders.exportCapNote', { max: MAX_EXPORT_RECORDS.toLocaleString() })}`
          : ''
      }`,
      { title: t('ecomOrders.exportConfirmTitle'), confirmText: t('common.export'), cancelText: t('common.cancel') }
    );
    if (!confirmed) return;

    const filterParams = buildFilterParams();
    startExport((job) => runOrderExport(filterParams, job), { title: t('ecomOrders.exportingOrders') });
  };

  const runOrderExport = async (filterParams, { signal, onTotal, onProgress }) => {
    const firstPage = await getEcomOrders({ ...filterParams, page: 1, limit: 1 }, { signal });
    const total     = Math.min(firstPage.total || 0, MAX_EXPORT_RECORDS);

    if (total === 0) {
      throw new Error(t('ecomOrders.noOrdersToExport'));
    }

    onTotal(total);
    const allOrders  = [];
    const totalPages = Math.ceil(total / EXPORT_BATCH_SIZE);

    for (let page = 1; page <= totalPages; page++) {
      if (signal.aborted) return;
      const response = await getEcomOrders(
        { ...filterParams, page, limit: EXPORT_BATCH_SIZE, ...(page > 1 && { skipCount: true }) },
        { signal }
      );
      allOrders.push(...(response.orders || []));
      onProgress(Math.min(allOrders.length, total));
    }
    if (signal.aborted) return;

    const headers = [
      'Order Number','Customer Name','Phone Numbers','City','Address','Email','Shop',
      'Products','Status','Status Reason','Payment Type','Payment Method','Payment Reference',
      'Subtotal','Total Discount','Delivery Charge','Tax','Grand Total','Courier',
      'Airwaybill Number','Courier Status','Dispatched Date','Created By','Confirmed By','Confirmed At',
      'Created Date','Updated Date'
    ];

    const escapeCell = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;

    const rows = allOrders.map(order => {
      const phoneNumbers = order.phoneNumbers
        ?.sort((a, b) => b.isPrimary - a.isPrimary)
        ?.map(p => p.phoneNumber)
        ?.join(', ') || '';

      const products = order.orderItems
        ?.map(item => `${item.Product?.name || 'N/A'} - ${item.quantity}`)
        ?.join(', ') || '';

      const paymentMethods = order.paymentRecords
        ?.map(pr => pr.PaymentMethod?.label).filter(Boolean).join(', ') || '';
      const paymentReferences = order.paymentRecords
        ?.map(pr => pr.reference).filter(Boolean).join(', ') || '';

      return [
        order.orderNumber,
        order.customerName,
        phoneNumbers,
        order.City?.name || '',
        order.address || '',
        order.customerEmail || '',
        order.Shop?.name || '',
        products,
        order.OrderStatus?.label || order.orderStatusKey,
        order.OrderStatusReason?.label || '',
        order.PaymentType?.label || order.paymentTypeKey || '',
        paymentMethods,
        paymentReferences,
        order.totalAmount || 0,
        order.totalDiscount || 0,
        order.deliveryCharge || 0,
        order.tax || 0,
        order.grandTotal || 0,
        order.assignedCourierAccount?.accountName || '',
        order.airwaybillNumber || '',
        order.courierStatus?.statusLabel || '',
        order.dispatchedDate ? formatDate(order.dispatchedDate) : '',
        order.createdBy?.name || '',
        order.confirmedByUser?.name || '',
        order.confirmedAt ? formatDateTime(order.confirmedAt) : '',
        formatDateTime(order.createdAt),
        formatDateTime(order.updatedAt)
      ].map(escapeCell).join(',');
    });

    const csvContent = [headers.map(escapeCell).join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url  = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = `orders-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // ─── Bulk Print Airwaybills ───────────────────────────────────────────────
  const handlePrintAirwaybills = () => {
    if (selectedOrders.length === 0) {
      showError(t('ecomOrders.selectAtLeastOneOrder') || 'Select at least one order');
      return;
    }
    const orderIds = selectedOrders.join(',');
    window.open(`/packing-batches/waybill-print?orderIds=${orderIds}`, '_blank');
  };

  // ─── Misc handlers ────────────────────────────────────────────────────────
  const handleCopyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopiedStates(prev => ({ ...prev, [key]: false })), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      showError('Failed to copy to clipboard');
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (searchValue) => {
    setFilters(prev => ({ ...prev, search: searchValue }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      shopIds: [],
      cityIds: [],
      statusKeys: [],
      paymentTypeKey: '',
      createdAtRange: { from: '', to: '' },
      dispatchedDateRange: { from: '', to: '' },
      shiftDateRange: { from: '', to: '' },
      shiftTypeIds: [],
      businessCourierIds: [],
      courierStatusIds: [],
      productIds: [],
      confirmedByUserIds: [],
      confirmedAtRange: { from: '', to: '' }
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => {
    const searchActive = Array.isArray(filters.search)
      ? filters.search.length > 0
      : filters.search !== '';

    return searchActive ||
      filters.shopIds.length > 0 ||
      filters.cityIds.length > 0 ||
      filters.statusKeys.length > 0 ||
      filters.paymentTypeKey !== '' ||
      filters.createdAtRange?.from !== '' ||
      filters.createdAtRange?.to !== '' ||
      filters.dispatchedDateRange?.from !== '' ||
      filters.dispatchedDateRange?.to !== '' ||
      filters.shiftDateRange?.from !== '' ||
      filters.shiftDateRange?.to !== '' ||
      filters.shiftTypeIds.length > 0 ||
      filters.businessCourierIds.length > 0 ||
      filters.courierStatusIds.length > 0 ||
      filters.productIds.length > 0 ||
      filters.confirmedByUserIds.length > 0 ||
      filters.confirmedAtRange?.from !== '' ||
      filters.confirmedAtRange?.to !== '';
  };

  const handleViewOrder  = (order) => { setSelectedOrderDetails(order); setShowDetailsModal(true); };
  const handleOrderUpdate = (updatedOrder) => {
    setSelectedOrderDetails(updatedOrder);
    setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
  };

  const getOrderStatusIcon = (statusKey) => {
    const icons = {
      pending:       <Clock size={14} />,
      confirmed:     <CheckCircle size={14} />,
      hold:          <Clock size={14} />,
      not_confirmed: <XCircle size={14} />,
      cancelled:     <XCircle size={14} />,
      in_courier:    <Truck size={14} />,
      settled:       <CheckCircle size={14} />,
      returned:      <XCircle size={14} />
    };
    return icons[statusKey] || <Clock size={14} />;
  };

  const canEditOrder = (order) => {
    if (isOfflineChannel(order.channel)) {
      return !order.OrderStatus?.isClosed && hasPermission('edit_offline_sales_order');
    }
    if (order.OrderStatus?.isInPacking  && !hasPermission('edit_in_packing_order'))  return false;
    if (order.OrderStatus?.isDispatched && !hasPermission('edit_dispatched_order'))  return false;
    return hasPermission('edit_ecom_order');
  };

  const editOrderPath = (order) =>
    isOfflineChannel(order.channel) ? `/offline-sales/orders/${order.id}/edit` : `/orders/${order.id}/edit`;

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // ─── Courier status options — inject "Not Updated" when list is non-empty ──
  const courierStatusOptions = referenceData.courierStatuses.length > 0
    ? [
        { id: NOT_UPDATED_VALUE, label: 'Not Updated' },
        ...referenceData.courierStatuses.map(cs => ({
          id: cs.id,
          label: `${cs.statusLabel || cs.statusCode} - ${cs.courierCompany?.name || 'N/A'}`
        }))
      ]
    : [];

  // ─── Filter definitions ───────────────────────────────────────────────────
  const filterDefinitions = [
    {
      key: 'shopIds',
      label: t('common.shop'),
      type: 'multiselect',
      placeholder: t('ecomOrders.allShops'),
      options: referenceData.shops,
      displayKey: 'name',
      valueKey: 'id',
      searchKey: 'name'
    },
    {
      key: 'cityIds',
      label: t('ecomOrders.city'),
      type: 'multiselect',
      placeholder: t('ecomOrders.allCities'),
      options: referenceData.cities,
      displayKey: 'name',
      valueKey: 'id',
      searchKey: 'name'
    },
    {
      key: 'statusKeys',
      label: t('ecomOrders.orderStatus'),
      type: 'multiselect',
      placeholder: t('ecomOrders.allStatuses'),
      options: referenceData.orderStatuses,
      displayKey: 'label',
      valueKey: 'key',
      searchKey: 'label'
    },
    {
      key: 'businessCourierIds',
      label: t('ecomOrders.businessCourier'),
      type: 'multiselect',
      placeholder: t('ecomOrders.allBusinessCouriers'),
      options: referenceData.businessCouriers.map(bc => ({
        id: bc.id,
        name: `${bc.accountName} (${bc.CourierCompany?.name || 'N/A'})`
      })),
      displayKey: 'name',
      valueKey: 'id',
      searchKey: 'name'
    },
    {
      key: 'courierStatusIds',
      label: t('ecomOrders.courierStatus'),
      type: 'multiselect',
      placeholder: t('ecomOrders.allCourierStatuses'),
      options: courierStatusOptions,
      displayKey: 'label',
      valueKey: 'id',
      searchKey: 'label'
    },
    {
      key: 'paymentTypeKey',
      label: t('ecomOrders.paymentType'),
      type: 'select',
      placeholder: t('ecomOrders.allPaymentTypes'),
      options: referenceData.paymentTypes.map(type => ({
        value: type.key,
        label: type.label
      }))
    },
    // Product filter
    {
      key: 'productIds',
      label: t('ecomOrders.products') || 'Products',
      type: 'custom',
      render: (value, onChange) => (
        <ProductFilter selectedProductIds={value} onChange={onChange} />
      )
    },
    // Created At date range picker
    {
      key: 'createdAtRange',
      label: t('ecomOrders.createdAt') || 'Created Date',
      type: 'custom',
      render: (value, onChange) => (
        <DateRangePicker
          value={value || { from: '', to: '' }}
          onChange={onChange}
          placeholder="All creation dates"
        />
      )
    },
    {
      key: 'confirmedByUserIds',
      label: t('ecomOrders.confirmedBy') || 'Confirmed By',
      type: 'multiselect',
      placeholder: 'All confirmers',
      options: referenceData.users,
      displayKey: 'name',
      valueKey: 'id',
      searchKey: 'name'
    },
    {
      key: 'confirmedAtRange',
      label: t('ecomOrders.confirmedAt') || 'Confirmed At',
      type: 'custom',
      render: (value, onChange) => (
        <DateRangePicker
          value={value || { from: '', to: '' }}
          onChange={onChange}
          placeholder="All confirmation dates"
        />
      )
    },
    // Dispatched Date range picker
    {
      key: 'dispatchedDateRange',
      label: t('ecomOrders.dispatchedDate') || 'Dispatched Date',
      type: 'custom',
      render: (value, onChange) => (
        <DateRangePicker
          value={value || { from: '', to: '' }}
          onChange={onChange}
          placeholder="All dispatched dates"
        />
      )
    },
    messageCenterSettings.enabled && hasPermission('view_message_center_shifts') ? {
      key: 'shiftDateRange',
      label: t('ecomOrders.shiftDate') || 'Shift Date',
      type: 'custom',
      render: (value, onChange) => (
        <DateRangePicker
          value={value || { from: '', to: '' }}
          onChange={onChange}
          placeholder="All shift dates"
        />
      )
    } : null,
    messageCenterSettings.enabled && hasPermission('view_message_center_shifts') ? {
      key: 'shiftTypeIds',
      label: t('ecomOrders.shiftType') || 'Shift Type',
      type: 'multiselect',
      placeholder: t('ecomOrders.allShiftTypes') || 'All shift types',
      options: referenceData.shiftTypes,
      displayKey: 'name',
      valueKey: 'id',
      searchKey: 'name'
    } : null
  ].filter(Boolean);

  // ─── Column definitions ───────────────────────────────────────────────────
  const columns = [
    {
      key: 'createdAt',
      header: t('ecomOrders.createdAt'),
      sortable: true,
      width: '150px',
      render: (order) => (
        <div className={styles.dateCell}>
          <div className={styles.dateInfo}><Calendar size={12} />{formatDate(order.createdAt)}</div>
          <div className={styles.timeInfo}>{formatTime(order.createdAt)}</div>
          {order.dispatchedDate && (
            <div className={styles.dispatchedDateInfo} title={t('ecomOrders.dispatchedDate') || 'Dispatched Date'}>
              <Truck size={12} />{formatDate(order.dispatchedDate)}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'customerName',
      header: t('ecomOrders.customer'),
      sortable: true,
      width: '220px',
      render: (order) => {
        const primaryPhone = order.phoneNumbers?.find(p => p.isPrimary) || order.phoneNumbers?.[0];
        const copyKey  = `phone-${order.id}`;
        const isCopied = copiedStates[copyKey];

        return (
          <div className={styles.customerInfo}>
            <div className={styles.customerName}><User size={14} />{truncateText(order.customerName, 20)}</div>
            <div className={styles.customerAddress}><MapPin size={12} />{order.City?.name || 'N/A'}</div>
            {primaryPhone && (
              <div className={styles.phoneNumberRow}>
                <a href={`tel:${primaryPhone.phoneNumber}`} className={styles.phoneNumber} onClick={(e) => e.stopPropagation()}>
                  <PhoneIcon size={12} />{primaryPhone.phoneNumber}
                </a>
                <button className={styles.copyButton} onClick={(e) => { e.stopPropagation(); handleCopyToClipboard(primaryPhone.phoneNumber, copyKey); }} title={isCopied ? 'Copied!' : 'Copy phone number'}>
                  {isCopied ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'courier',
      header: t('ecomOrders.courier'),
      width: '180px',
      render: (order) => {
        const copyKey  = `awb-${order.id}`;
        const isCopied = copiedStates[copyKey];

        return (
          <div className={styles.courierInfo}>
            {order.airwaybillNumber && (
              <div className={styles.airwaybillWrapper}>
                <button
                  type="button"
                  className={styles.airwaybillNumber}
                  onClick={(e) => { e.stopPropagation(); setTrackingOrder(order); }}
                  title={t('ecomOrders.viewPackageTimeline')}
                >
                  {order.airwaybillNumber}
                </button>
                <button className={styles.copyButton} onClick={(e) => { e.stopPropagation(); handleCopyToClipboard(order.airwaybillNumber, copyKey); }} title={isCopied ? 'Copied!' : 'Copy airwaybill number'}>
                  {isCopied ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
            )}
            {order.assignedCourierAccount ? (
              <div className={styles.courierName}><Truck size={12} />{order.assignedCourierAccount.accountName}</div>
            ) : (
              <div className={styles.courierName}><Truck size={12} />{t('common.na')}</div>
            )}
          </div>
        );
      }
    },
    {
      key: 'status',
      header: t('ecomOrders.status'),
      sortable: true,
      width: '160px',
      render: (order) => (
        <div className={styles.statusCell}>
          <div
            className={styles.statusBadge}
            style={{
              backgroundColor: order.OrderStatus?.colorCode ? `${order.OrderStatus.colorCode}20` : '#F59E0B20',
              borderColor: order.OrderStatus?.colorCode || '#F59E0B',
              color: order.OrderStatus?.colorCode || '#92400e'
            }}
          >
            {getOrderStatusIcon(order.OrderStatus?.key)}
            <span>{order.OrderStatus?.label || order.orderStatusKey}</span>
          </div>
          {order.courierStatus && (
            <div className={styles.courierStatusBadge} title={order.courierStatus.statusLabel}>
              <span className={styles.courierStatusText}>{order.courierStatus.statusLabel}</span>
            </div>
          )}
        </div>
      )
    },
    {
      key: 'grandTotal',
      header: t('ecomOrders.total'),
      sortable: true,
      width: '120px',
      align: 'right',
      render: (order) => <div className={styles.totalAmount}>{formatCurrency(order.grandTotal)}</div>
    },
    {
      key: 'products',
      header: t('ecomOrders.products'),
      width: '220px',
      render: (order) => (
        <div className={styles.productsInfo}>
          {order.orderItems?.slice(0, 3).map((item, idx) => (
            <div key={idx} className={styles.productItem}>{item.Product?.name || 'N/A'} × {item.quantity}</div>
          ))}
          {order.orderItems?.length > 3 && (
            <div className={styles.productItem}>+{order.orderItems.length - 3} more</div>
          )}
        </div>
      )
    },
    {
      key: 'paymentType',
      header: t('ecomOrders.paymentType') || 'Payment Type',
      width: '140px',
      render: (order) => {
        const uniqueMethods = Array.from(
          (order.paymentRecords || [])
            .filter(pr => pr.PaymentMethod?.key)
            .reduce((map, pr) => {
              const key = pr.PaymentMethod.key;
              const existing = map.get(key);
              // A method only counts as verified for the badge if every record using it is verified.
              const verified = existing ? existing.verified && !!pr.isVerified : !!pr.isVerified;
              map.set(key, { method: pr.PaymentMethod, verified });
              return map;
            }, new Map())
            .values()
        );

        return (
          <div className={styles.paymentTypeCell}>
            <div className={styles.paymentTypeBadge}>
              {order.PaymentType?.label || order.paymentTypeKey || '—'}
            </div>
            {uniqueMethods.length > 0 && (
              <div className={styles.paymentMethodInfo}>
                {uniqueMethods.map(({ method, verified }) => (
                  <PaymentMethodIcon key={method.key} iconFileName={method.iconFileName} label={method.label} verified={verified} />
                ))}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'shop',
      header: t('ecomOrders.shop'),
      width: '140px',
      render: (order) => (
        <div className={styles.shopBadge}><Store size={12} />{order.Shop?.name || 'N/A'}</div>
      )
    },
    {
      key: 'createdBy',
      header: t('ecomOrders.createdBy'),
      width: '140px',
      render: (order) => (
        <div className={styles.createdByInfo}><User size={12} />{order.createdBy?.name || 'N/A'}</div>
      )
    },
    {
      key: 'confirmedBy',
      header: t('ecomOrders.confirmedBy') || 'Confirmed By',
      width: '140px',
      render: (order) => (
        <div className={styles.createdByInfo}>
          <CheckCircle size={12} />
          {order.confirmedByUser?.name || <span className={styles.naText}>—</span>}
        </div>
      )
    },
    {
      key: 'confirmedAt',
      header: t('ecomOrders.confirmedAt') || 'Confirmed At',
      width: '150px',
      render: (order) => (
        <div className={styles.createdByInfo}>
          <Calendar size={12} />
          {order.confirmedAt ? formatDateTime(order.confirmedAt) : <span className={styles.naText}>—</span>}
        </div>
      )
    },
    {
      key: 'orderNumber',
      header: t('ecomOrders.orderNumber'),
      sortable: true,
      width: '140px',
      render: (order) => {
        const copyKey  = `order-${order.id}`;
        const isCopied = copiedStates[copyKey];

        return (
          <div className={styles.orderNumberCell}>
            <div className={styles.orderNumber} onClick={(e) => { e.stopPropagation(); handleViewOrder(order); }}>
              #{order.orderNumber}
            </div>
            <button className={styles.copyButton} onClick={(e) => { e.stopPropagation(); handleCopyToClipboard(order.orderNumber, copyKey); }} title={isCopied ? 'Copied!' : 'Copy order number'}>
              {isCopied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        );
      }
    }
  ];

  // ─── Table actions ────────────────────────────────────────────────────────
  const tableActions = {
    render: (order) => (
      <div className={styles.actionsWrapper}>
        {hasPermission('view_ecom_order') && (
          <button type="button" className={`${styles.actionButton} ${styles.viewButton}`} title={t('common.view')} onClick={(e) => { e.stopPropagation(); handleViewOrder(order); }}>
            <Eye size={14} />
          </button>
        )}
        {canEditOrder(order) && (
          <Link to={editOrderPath(order)} className={`${styles.actionButton} ${styles.editButton}`} title={t('common.edit')} onClick={(e) => e.stopPropagation()}>
            <Edit size={14} />
          </Link>
        )}
      </div>
    ),
    width: '100px',
    frozen: true
  };

  // ─── Early returns ────────────────────────────────────────────────────────
  if (isLoading && orders.length === 0) {
    return (
      <div className={styles.orderListPage}>
        <PageHeader title={t('ecomOrders.allOrders')} description={t('ecomOrders.viewManageOrders')} icon={ShoppingCart} />
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinnerLarge}></div>
          <h3>{t('common.loading')}</h3>
          <p>{t('ecomOrders.loadingOrders')}</p>
        </div>
      </div>
    );
  }

  if (loadingError) {
    return (
      <div className={styles.orderListPage}>
        <PageHeader title={t('ecomOrders.allOrders')} description={t('ecomOrders.viewManageOrders')} icon={ShoppingCart} />
        <div className={styles.errorState}>
          <AlertCircle className={styles.errorIcon} size={48} />
          <h3>{t('ecomOrders.unableToLoadOrders')}</h3>
          <p>{loadingError}</p>
          <button onClick={fetchOrders} className={styles.retryButton}><RefreshCw size={16} />{t('common.retry')}</button>
        </div>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className={styles.orderListPage}>
      <PageHeader
        title={t('ecomOrders.allOrders')}
        description={t('ecomOrders.viewManageOrders')}
        icon={ShoppingCart}
        actions={
          <>
            {hasPermission('manage_order_form_fields') && (
              <button type="button" onClick={() => setShowFieldSettings(true)} className={styles.secondaryButton}>
                <SlidersHorizontal size={18} />
                {t('ecomOrders.orderFieldSettings')}
              </button>
            )}
            {hasPermission('create_ecom_order') && (
              <Link to="/orders/create" className={styles.primaryButton}>
                <Plus size={20} />
                {t('ecomOrders.addOrder')}
              </Link>
            )}
          </>
        }
      />

      {/* Bulk Actions Bar */}
      {selectedOrders.length > 0 && (
        <div className={styles.bulkActionsBar}>
          <div className={styles.bulkActionsLeft}>
            <span className={styles.selectedCount}>
              {selectedOrders.length} {selectedOrders.length === 1 ? t('ecomOrders.orderSelected') || 'order selected' : t('ecomOrders.ordersSelected') || 'orders selected'}
            </span>
          </div>
          <div className={styles.bulkActionsRight}>
            <button onClick={handlePrintAirwaybills} className={styles.bulkActionButton}>
              <Printer size={15} />
              {t('ecomOrders.printAirwaybills') || 'Print Airwaybills'}
            </button>
            <button onClick={() => setSelectedOrders([])} className={styles.bulkCancelButton}>
              {t('common.clearSelection') || 'Clear Selection'}
            </button>
          </div>
        </div>
      )}

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        onExport={handleExport}
        showExport={true}
        exportButtonText={t('common.export')}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('ecomOrders.searchOrdersPlaceholder'),
          multiSearch: true
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions
        }}
      />

      <DataTable
        data={orders}
        columns={columns}
        actions={tableActions}
        selectable
        selectedRows={selectedOrders}
        onSelectionChange={setSelectedOrders}
        onRowClick={handleViewOrder}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={hasActiveFilters() ? t('ecomOrders.noOrdersFound') : t('ecomOrders.noOrdersYet')}
        emptyDescription={hasActiveFilters() ? t('ecomOrders.noOrdersFoundDescription') : t('ecomOrders.startCreatingOrders')}
        emptyIcon={<ShoppingCart size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={handleClearFilters} className={styles.secondaryButton}>{t('common.clearFilters')}</button>
          ) : hasPermission('create_ecom_order') ? (
            <button onClick={() => navigate('/orders/create')} className={styles.primaryButton}>
              <Plus size={20} />{t('ecomOrders.addOrder')}
            </button>
          ) : null
        }
      />

      {showDetailsModal && selectedOrderDetails && (
                isOfflineChannel(selectedOrderDetails.channel) ? (
          <OfflineSalesOrderDetailsModal
            order={selectedOrderDetails}
            onClose={() => { setShowDetailsModal(false); setSelectedOrderDetails(null); }}
          />
        ) : (
          <OrderDetailsModal
            order={selectedOrderDetails}
            onClose={() => { setShowDetailsModal(false); setSelectedOrderDetails(null); }}
            onUpdate={handleOrderUpdate}
          />
        )
      )}

      {trackingOrder && (
        <AirwaybillTrackingModal
          orderId={trackingOrder.id}
          airwaybillNumber={trackingOrder.airwaybillNumber}
          courierName={trackingOrder.assignedCourierAccount?.accountName}
          onClose={() => setTrackingOrder(null)}
        />
      )}

      {showFieldSettings && (
        <OrderFormFieldSettingsModal onClose={() => setShowFieldSettings(false)} />
      )}
    </div>
  );
};

export default OrderListPage;